import { Box, useTheme } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { tokens } from "../../../../../theme";

import Text from "../../../../../components/theme/text/Text";

import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from "react-i18next";
import ButtonGroup from "../../../../../components/theme/buttons/ButtonGroup";
import Shbutton from "../../../../../components/theme/buttons/Shbutton";
import { useGetRelationAddressesQuery } from "../../../../../newapi/relationaddress/relationaddressSlice";
import MAddRelationAddress from "../../../../../components/global/Modals/MAddRelationAddress";
import { RelationAddressCard } from "../../../../../components/global/cards/relationaddresses/RelationAddressCard";
import SidepanelLoader from "../../../../../components/theme/sidepanel2/SidepanelLoader";
import ShTextSkeleton from "../../../../../components/theme/skeleton/ShTextSkeleton";
import { selectUserPermission } from "../../../../../newapi/user/authentication/authSlice";
import { UserPrivileges } from "../../../../../utils/permissions";
import { useSelector } from "react-redux";
import ShDataGrid3 from "../../../../../components/theme/datagrid3/ShDataGrid3";

const SpRelationAddressDetails = SidepanelLoader(() => import("../../../../../components/global/Sidepanels/SpRelationAddressDetails/SpRelationAddressDetails"));

const GridActions = (props, i18n) => {
    const theme = useTheme();
    useEffect(() => {},[props.open]);
    const {t} = useTranslation();
    return (
        <ButtonGroup version={2}>
            {props.hasCreatePermission && (
            <Shbutton
                onClick={() => props.handleOpen(true)}
                className={"group-btn"}
                color={theme.palette.mode === "dark" ? "blue" : "primary"}
                variant="contained"
                endIcon={<AddIcon/>}>{t("new_address")}</Shbutton>
            )}
        </ButtonGroup>
    )
}

let gridOptions = {
    export:true,
    disableCustomActionsOnPhone: true,
    isSidepanel: true,
};

const All = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [data, setData] = useState([]);
    const [isExtended,setIsExtended] = useState(false);

    const hasCreatePermission = useSelector((state) =>
        selectUserPermission(state, [UserPrivileges.RP_ORDER_MANAGE])
    );

    const [addModalOpen, setAddModalOpen] = useState(false);
    const addModal = {
        open: addModalOpen,
        setOpen: setAddModalOpen
    }

    const handleClose = () => {
        setAddModalOpen(false);
    };

    const [relationAddressId, setRelationAddressId] = useState();
    const [spLoading, setSpLoading] = useState();
    const relationAddressIdRef = useRef(relationAddressId);

    const getRelationAddressDetails = (params) => {
        if (!params) return;

        const newRelationAddressId = parseInt(params.row.id);
        // Only update state if the relationAddressId has actually changed
        if (relationAddressIdRef.current !== newRelationAddressId) {

            if(!spLoading) {
                setSpLoading(true);
            }
            setRelationAddressId(newRelationAddressId);
            relationAddressIdRef.current = newRelationAddressId; // Update the ref to the new value
        }
        setIsExtended(true);
    };

    useEffect(() => {
        props.replaceView({
            ...props.config,
            gridOptions:gridOptions,
            mobileActions:<GridActions hasCreatePermission={hasCreatePermission} handleOpen={() => setAddModalOpen(true)}></GridActions>
        });

        return () => {
            // console.log("unmounting warehouse");
        }
    },[]);

    const columns = [
        {field: "name", headerName: t("name"), flex:2, cellClassName: "name", renderCell : (cellValues) => {
            if(cellValues.row.skeleton) {
                return <ShTextSkeleton/>
            }

            const fullName = `${cellValues.row.firstName ?? ""} ${cellValues.row.lastName ?? ""}`.trim();
            return (
                <Text semibold>{fullName !== "" ? fullName : cellValues.row.company}</Text>
            )
        }},
        {field: "company", headerName: t("company"), flex:2, cellClassName: "company", renderCell : (cellValues) => {
            if(cellValues.row.skeleton) {
                return <ShTextSkeleton/>
            }

            return (
                <Text>{cellValues.row.company}</Text>
            )
        }},
        {field: "phone", headerName: t("phone"), flex:2, cellClassName: "phone", renderCell : (cellValues) => {
            if(cellValues.row.skeleton) {
                return <ShTextSkeleton/>
            }

            return (
                <Text>{cellValues.row.phone}</Text>
            )
        }},
        {field: "email", headerName: t("email"), flex:2, cellClassName: "email", renderCell : (cellValues) => {
            if(cellValues.row.skeleton) {
                return <ShTextSkeleton/>
            }

            return (
                <Text>{cellValues.row.email}</Text>
            )
        }},
        {field: "addressLine1", headerName: t("address"), flex:2, cellClassName: "address", renderCell : (cellValues) => {
            if(cellValues.row.skeleton) {
                return <ShTextSkeleton/>
            }

            const cityString = `${cellValues.row.postalCode ?? ""} ${cellValues.row.city ?? ""} ${cellValues.row.country && cellValues.row.country !== null && cellValues.row.country !== "" ? `(${cellValues.row.country.toUpperCase()})` : ""}`;

            return (
                <Text>{`${cellValues.row.addressLine1 ?? ""}, ${cityString}`}</Text>
            )
        }},
        {
            field: "phoneView",
            headerName: "PhoneView",
            cellClassName: "phone-column--cell",
            renderCell: (cellValues) => {
            if(cellValues.row.skeleton) {
                return <ShTextSkeleton/>
            }

                return <RelationAddressCard onClick={() => getRelationAddressDetails(cellValues)} skeleton={cellValues.row.skeleton ?? false} {...cellValues.row}/>
            }
        }
    ];

    return (
        <Box
            sx={{
                height:1,
                "@media screen and (max-width: 47.9375em)" : {
                    paddingTop:0,
                    paddingX:0,
                }
            }}
        >
            <ShDataGrid3
                gridKey="relations.settings.addresses"
                disableViewSaving
                key={theme.palette.mode}
                content={useGetRelationAddressesQuery}
                gridActions={<GridActions hasCreatePermission={hasCreatePermission} handleOpen={() => setAddModalOpen(true)}></GridActions>}
                title={t("overview")}
                gridOptions={gridOptions}
                columns={columns}
                sortModel={{field: columns[0].field,type:"ASC"}}
                onRowClick={getRelationAddressDetails}
                rows={data}>
            </ShDataGrid3>

            <MAddRelationAddress onOpen={() => addModal.setOpen(true)} onClose={() => addModal.setOpen(false)} open={addModal.open} handleClose={handleClose}/>

            {spLoading &&
                    <SpRelationAddressDetails
                        isExtended={isExtended}
                        setIsExtended={setIsExtended}
                        relationAddressId={relationAddressId}
                    />
            }

        </Box>
    )
};

export default All;
